import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { MdSave, MdAdd, MdDelete, MdSearch } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    Catalog,
    CatalogPartner,
    getVariationsPricing,
    setVariationPartners,
    SetPartnerData,
} from '@/lib/api/catalog/catalog-api'
import {
    formatCentsToDisplay,
    parseCurrencyToCents,
    recalcPriceTriple,
} from '@/lib/form-state/money'

// Preço em centavos → null quando vazio (para o payload).
const maskedToCentsOrNull = (masked: string): number | null => {
    const cents = parseCurrencyToCents(masked)
    return cents > 0 ? cents : null
}

// ==================== Tipos de estado local ====================

interface PartnerRow {
    partnerId: number
    partnerName: string
    partnerLocation?: string
    price: string // em reais (input)
    sellingPrice: string
    markupPercentage: string
}

function rowFromPartner(p: CatalogPartner): PartnerRow {
    return {
        partnerId: p.partnerId,
        partnerName: p.partner?.name || `Parceiro ${p.partnerId}`,
        partnerLocation: [p.partner?.city, p.partner?.state].filter(Boolean).join(' / '),
        price: formatCentsToDisplay(p.price),
        sellingPrice: formatCentsToDisplay(p.sellingPrice),
        markupPercentage: p.markupPercentage != null ? String(p.markupPercentage) : '',
    }
}

// Estado por variação (catalogId da filha) -> linhas de parceiro.
type RowsByVariation = Record<number, PartnerRow[]>

export default function VariationsPricingPage() {
    const { user } = useAuth()
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')

    const filters = useMemo(() => ({ search }), [search])

    const fetchFn = useCallback(
        (params: any) => getVariationsPricing(user!.companyId!, params),
        [user?.companyId]
    )

    const { items, loading, page, limit, total, setPage, reload } = usePaginatedPage<
        Catalog,
        typeof filters
    >({
        fetchFn,
        filters,
        limit: 10,
        enabled: !!user?.companyId,
    })

    // Estado editável das linhas de parceiro, por variação.
    const [rows, setRows] = useState<RowsByVariation>({})
    const [savingId, setSavingId] = useState<number | null>(null)

    // Hidrata o estado local sempre que a página (items) muda.
    useEffect(() => {
        const next: RowsByVariation = {}
        for (const parent of items) {
            for (const child of parent.variationChildren || []) {
                next[child.id] = (child.partners || []).map(rowFromPartner)
            }
        }
        setRows(next)
    }, [items])

    // Edita custo/venda/markup com máscara R$ + recálculo bidirecional (igual ao
    // formulário de catálogo). Ver @/lib/form-state/money.
    const updateRowPrice = (
        variationId: number,
        idx: number,
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => {
        setRows(prev => {
            const list = [...(prev[variationId] || [])]
            list[idx] = recalcPriceTriple(list[idx], field, value)
            return { ...prev, [variationId]: list }
        })
    }

    const removeRow = (variationId: number, idx: number) => {
        setRows(prev => {
            const list = [...(prev[variationId] || [])]
            list.splice(idx, 1)
            return { ...prev, [variationId]: list }
        })
    }

    const addPartner = (
        variationId: number,
        partnerId: number | null,
        partner: { name?: string; city?: string | null; state?: string | null } | null
    ) => {
        if (!partnerId) return
        setRows(prev => {
            const list = prev[variationId] || []
            if (list.some(r => r.partnerId === partnerId)) {
                toast.info('Parceiro já adicionado nesta variação')
                return prev
            }
            const newRow: PartnerRow = {
                partnerId,
                partnerName: partner?.name || `Parceiro ${partnerId}`,
                partnerLocation: [partner?.city, partner?.state].filter(Boolean).join(' / '),
                price: '',
                sellingPrice: '',
                markupPercentage: '',
            }
            return { ...prev, [variationId]: [...list, newRow] }
        })
    }

    const handleSave = async (variationId: number) => {
        const list = rows[variationId] || []
        const payload: SetPartnerData[] = list.map(r => ({
            partnerId: r.partnerId,
            price: maskedToCentsOrNull(r.price),
            sellingPrice: maskedToCentsOrNull(r.sellingPrice),
            markupPercentage: r.markupPercentage.trim()
                ? Number(r.markupPercentage.replace(',', '.'))
                : null,
        }))
        setSavingId(variationId)
        try {
            const res = await setVariationPartners(variationId, payload)
            if (res.success) {
                toast.success('Parceiros salvos')
                setRows(prev => ({
                    ...prev,
                    [variationId]: (res.result || []).map(rowFromPartner),
                }))
            } else {
                toast.error(res.error || 'Falha ao salvar')
            }
        } catch {
            toast.error('Erro ao salvar parceiros')
        } finally {
            setSavingId(null)
        }
    }

    const submitSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setSearch(searchInput.trim())
    }

    if (!user?.companyId) {
        return <div className="p-6 text-base-content/60">Carregando empresa...</div>
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="mb-4">
                <h1 className="text-xl font-bold">Precificação de variações</h1>
                <p className="text-sm text-base-content/60">
                    Adicione parceiros e preços (compra, venda e markup) a cada variação. As
                    variações são agrupadas pelo item pai. Salve variação por variação.
                </p>
            </div>

            <form onSubmit={submitSearch} className="mb-4 flex gap-2">
                <label className="input input-bordered input-sm flex items-center gap-2 flex-1 max-w-md">
                    <MdSearch className="opacity-60" />
                    <input
                        type="text"
                        className="grow"
                        placeholder="Buscar item pai por nome..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </label>
                <button type="submit" className="btn btn-sm btn-secondary">
                    Buscar
                </button>
                {search && (
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => {
                            setSearch('')
                            setSearchInput('')
                        }}
                    >
                        Limpar
                    </button>
                )}
            </form>

            {loading ? (
                <div className="py-12 text-center text-base-content/60">Carregando...</div>
            ) : items.length === 0 ? (
                <div className="py-12 text-center text-base-content/60">
                    Nenhum item com variações encontrado.
                </div>
            ) : (
                <div className="space-y-6">
                    {items.map(parent => (
                        <div key={parent.id} className="card bg-base-100 border border-base-300">
                            <div className="card-body p-4">
                                <div className="flex items-baseline gap-2 border-b border-base-200 pb-2 mb-2">
                                    <h2 className="font-semibold text-base">{parent.name}</h2>
                                    <span className="badge badge-ghost badge-sm">
                                        {parent.category}
                                    </span>
                                    <span className="text-xs text-base-content/50">
                                        {(parent.variationChildren || []).length} variações
                                    </span>
                                </div>

                                <div className="space-y-4">
                                    {(parent.variationChildren || []).map(child => {
                                        const list = rows[child.id] || []
                                        return (
                                            <div
                                                key={child.id}
                                                className="rounded-lg bg-base-200/40 p-3"
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="font-medium text-sm">
                                                        {child.name}
                                                        {child.tussCode && (
                                                            <span className="ml-2 text-xs text-base-content/50">
                                                                TUSS {child.tussCode}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="btn btn-xs btn-secondary"
                                                        disabled={savingId === child.id}
                                                        onClick={() => handleSave(child.id)}
                                                    >
                                                        <MdSave />
                                                        {savingId === child.id
                                                            ? 'Salvando...'
                                                            : 'Salvar'}
                                                    </button>
                                                </div>

                                                {list.length > 0 && (
                                                    <div className="overflow-x-auto">
                                                        <table className="table table-xs">
                                                            <thead>
                                                                <tr>
                                                                    <th>Parceiro</th>
                                                                    <th className="w-28">
                                                                        Compra (R$)
                                                                    </th>
                                                                    <th className="w-28">
                                                                        Venda (R$)
                                                                    </th>
                                                                    <th className="w-24">
                                                                        Markup %
                                                                    </th>
                                                                    <th className="w-10"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {list.map((r, idx) => (
                                                                    <tr key={r.partnerId}>
                                                                        <td>
                                                                            <div className="text-xs font-medium">
                                                                                {r.partnerName}
                                                                            </div>
                                                                            {r.partnerLocation && (
                                                                                <div className="text-[10px] text-base-content/50">
                                                                                    {r.partnerLocation}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                inputMode="numeric"
                                                                                placeholder="R$ 0,00"
                                                                                className="input input-xs input-bordered w-full"
                                                                                value={r.price}
                                                                                onChange={e =>
                                                                                    updateRowPrice(
                                                                                        child.id,
                                                                                        idx,
                                                                                        'price',
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                inputMode="numeric"
                                                                                placeholder="R$ 0,00"
                                                                                className="input input-xs input-bordered w-full"
                                                                                value={r.sellingPrice}
                                                                                onChange={e =>
                                                                                    updateRowPrice(
                                                                                        child.id,
                                                                                        idx,
                                                                                        'sellingPrice',
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                className="input input-xs input-bordered w-full"
                                                                                value={r.markupPercentage}
                                                                                onChange={e =>
                                                                                    updateRowPrice(
                                                                                        child.id,
                                                                                        idx,
                                                                                        'markupPercentage',
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <button
                                                                                className="btn btn-ghost btn-xs text-error"
                                                                                onClick={() =>
                                                                                    removeRow(child.id, idx)
                                                                                }
                                                                                title="Remover parceiro"
                                                                            >
                                                                                <MdDelete />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                <div className="mt-2 flex items-center gap-2">
                                                    <MdAdd className="text-base-content/50" />
                                                    <div className="w-full max-w-md">
                                                        <PartnerSelector
                                                            key={`add-${child.id}-${list.length}`}
                                                            value={null}
                                                            size="sm"
                                                            label=""
                                                            placeholder="Adicionar parceiro..."
                                                            onChange={(pid, partner) =>
                                                                addPartner(
                                                                    child.id,
                                                                    pid,
                                                                    partner as any
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {total > limit && (
                <div className="mt-6">
                    <PaginationControls
                        page={page}
                        limit={limit}
                        total={total}
                        onPageChange={setPage}
                    />
                </div>
            )}

            <div className="mt-4 text-right">
                <button className="btn btn-ghost btn-xs" onClick={reload}>
                    Recarregar
                </button>
            </div>
        </div>
    )
}
